import { Button, Checkbox, createMuiTheme, FormControlLabel, Grid, makeStyles, MenuItem, ThemeProvider } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import React, { useEffect, useReducer, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { QuoteFormConfigDTO } from '../DTO/QuoteFormConfigDTO';
import BackEndService from './back-end.service';
import jCaptcha from 'js-captcha';
import { FieldDTO } from '../DTO/FieldDTO';
import clsx from "clsx";
import { deepMerge, dispatchCustomEvent } from '../helpers';

const formReducer = (state, event) => {
    return deepMerge(state, {
        [event.name]: event.value,
    });
};

const useStyles = makeStyles((theme) => {
    return ({
        root: {
            paddingTop: theme.spacing(3),
        },
        checkboxIcon: {
            borderRadius: 3,
            width: 16,
            height: 16,
            boxShadow: "inset 0 0 0 1px #fff, inset 0 -1px 0 #fff",
            backgroundColor: "transperant",
            "input:disabled ~ &": {
                boxShadow: "none",
                background: "rgba(206,217,224,.5)"
            }
        },
        checkboxCheckedIcon: {
            borderRadius: 3,
            width: 16,
            height: 16,
            backgroundColor: '#f26930',
            boxShadow: `inset 0 0 0 1px #f26930, inset 0 -1px 0 #f26930`,
            "&:before": {
                display: "block",
                width: 16,
                height: 16,
                backgroundImage:
                    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
                    " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
                    "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
                content: '""'
            },
            "input:hover ~ &": {
                backgroundColor: '#f26930'
            }
        },
        paper: {
            padding: theme.spacing(2),
            textAlign: 'center',
            color: theme.palette.text.secondary,
        },
        serverError: {
            marginTop: 10,
            marginBottom: 30,
            color: '#f00',
            backgroundColor: '#fbc7c6',
            borderRadius: 5,
            padding: 10,
        },
        bootstrapField: {
            backgroundColor: '#ffffff',
            color: '#000',
        },
        bootstrapFieldError: {
            color: '#f00',
        },
        submitButton: {
            borderRadius: 5,
            padding: 20,
            paddingTop: 10,
            paddingBottom: 10,
        },
        selectItem: {
            color: theme.palette.text.secondary,
        },
        request_quote_jCaptchaCanvas: {
            borderWidth: 1,
            borderRadius: 5,
            borderStyle: 'solid',
            borderColor: '#000',
            backgroundColor: '#cecece',
            padding: 10,
            marginRight: 10,
            // marginBottom: 10,
        },
    })
});

const generateThemeColors = (config: QuoteFormConfigDTO) => {
    return {
        palette: {
            typography: {
                fontFamily: 'Raleway',
            },
            text: {
                primary: config.layout.theme.text,
                secondary: '#000000',
            },
            primary: {
                light: config.layout.theme.primary,
                main: config.layout.theme.primary,
                dark: config.layout.theme.primary,
                contrastText: config.layout.theme.primaryContrastText,
            },
            secondary: {
                light: config.layout.theme.accent,
                main: config.layout.theme.accent,
                dark: config.layout.theme.accent,
                contrastText: config.layout.theme.accentContrastText,
            },
        },
    };
};

const QuoteForm = ({ config, backEndService }: { config: QuoteFormConfigDTO; backEndService: BackEndService }) => {
    // Styles
    const formRef = React.createRef<HTMLFormElement>();
    const theme = createMuiTheme(generateThemeColors(config));
    const classes = useStyles();
    const grecaptcha = React.createRef<HTMLDivElement>();
    const [formData, setFormData] = useReducer(formReducer, {});
    const [fields, setFields] = useState(config.layout.fields);
    const [serverError, setServerError] = useState(null);
    const [submitStatus, setSubmitStatus] = useState<'submit-no' | 'submit-success' | 'submit-error'>('submit-no');

    // Captcha properties.
    const [captchaInstance, setCaptchaInstance] = useState<any>();
    const [captchaStatus, setCaptchaStatus] = useState(false);

    const getPattern = (fieldName: string) => {
        let regex = config.validation[fieldName];

        return regex;

        // TODO WHEN WE HAVE ENDPOINT FOR THE CONFIGURATION WE GOING TO NEED THIS.
        let flags = regex.replace(/.*\/([gimy]*)$/, '$1');
        let newPattern = '';

        if (flags) {
            newPattern = regex.replace(new RegExp('^/(.*?)/' + flags + '$'), '$1');
        } else {
            newPattern = regex.replace(new RegExp('^/(.*?)/$'), '$1');
        }

        return new RegExp(newPattern, flags);
    };

    const validationMethod = (data) => {
        let isFormValid = true;

        const validateFields = fields.map((field: FieldDTO) => {
            if (field.required) {
                if (field.type == 'checkboxes') {
                    field.error = false;
                    return field;
                }

                if ((!data[field.name] || !getPattern(field.name).test(data[field.name]))) {
                    field.error = true;
                    isFormValid = false;
                    return field;
                } else {
                    field.error = false;
                    return field;
                }
            } else {

                if (field.type == 'checkboxes') {
                    field.error = false;
                    return field;
                }

                if ((data[field.name] && getPattern(field.name).test(data[field.name])) || !(data[field.name])) {
                    field.error = false;
                    return field;
                } else {
                    field.error = true;
                    isFormValid = false;
                    return field;
                }
            }
        });

        setFields(validateFields);

        return isFormValid;
    };

    const validationCaptcha = () => {
        return new Promise((res) => {
            if (config.captcha.type === 'google') {
                window['grecaptcha'].reset(captchaInstance);
                window['grecaptcha'].execute(captchaInstance);
                res(false); // * the submit data is handle from captcha callback.
            } else {
                if (captchaInstance) {
                    captchaInstance.$el = document.querySelector('.request_quote_jCaptcha input');
                    captchaInstance.options.callback = (response: 'error' | 'success') => {
                        if (response === 'error') {
                            setCaptchaStatus(true);
                            res(false);
                        }

                        if (response === 'success') {
                            setCaptchaStatus(false);
                            res(true);
                        }
                    };

                    captchaInstance.validate();
                } else {
                    res(false);
                }
            }
        });
    };

    const randomReferenceNumber = (portion = 3) => {
        let referenceNumber = Math.random().toString(36).slice(-5);
        for (let index = 0; index < portion; index++) {
            referenceNumber += '-' + Math.random().toString(36).slice(-5);
        }
        return referenceNumber;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const isFormValid = validationMethod(formData);
        if (isFormValid) {
            formRef.current.sosFormSubmitData = formData;
            const isCaptchaValid = await validationCaptcha(); // ! GOOGLE CAPTCHA RETURN EVERY TIME FALSE.
            // const isCaptchaValid = true; // ! DEBUG - GOOGLE CAPTCHA RETURN EVERY TIME FALSE.

            if (isCaptchaValid) {
                const data = formData;
                submitData(data);
            }
        }
    };

    const submitData = (data) => {
        data.reference = randomReferenceNumber();
        data.source_abbr = config.source_abbr;
        backEndService
            .post('/external/quotes', {}, data)
            .then((response) => {
                if (response.error) {
                    setServerError(response);
                    setSubmitStatus('submit-error');
                    dispatchCustomEvent('serviceos-forms-error', {
                        submit_error: response.error,
                        source_abbr: config.source_abbr
                    })
                } else {
                    setServerError(null);
                    dispatchCustomEvent('serviceos-forms-success', {
                        submit_data: data,
                        source_abbr: config.source_abbr
                    })
                    setSubmitStatus('submit-success');
                }
            })
            .catch((error) => {
                setServerError({
                    error: [{ message: 'Something went wrong' }],
                });
                dispatchCustomEvent('serviceos-forms-error', {
                    submit_error: [{ message: 'Something went wrong' }],
                    source_abbr: config.source_abbr
                })
                setSubmitStatus('submit-error');
            });
    };

    const handleChange = (event) => {

        setFormData({
            name: event.target.name,
            value: event.target.value,
        });
    };

    const handleChangeCheckbox = (field => {
        return (event) => {
            setFormData({
                name: field.name,
                value: {
                    [event.target.name]:
                        event.target.checked,
                },
            });
        }
    })

    const renderFields = (field: FieldDTO) => {
        switch (field.type) {
            case 'select-field':
                return (
                    <Grid key={field.name} item xs={12}>
                        {config.layout.theme.fieldsType == 'bootstrap' && field.label && (
                            <div style={{ marginBottom: 5 }}>
                                <label className={field.error ? classes.bootstrapFieldError : ''} htmlFor="{field.name}">
                                    {field.label}
                                    {field.required && field.label ? '*' : ''}
                                </label>
                            </div>
                        )}
                        <TextField
                            id={field.name}
                            required={field.required}
                            label={config.layout.theme.fieldsType == 'material' ? field.label : ''}
                            select
                            SelectProps={{
                                MenuProps: {
                                    style: { zIndex: config.layout.type === 'popup' ? 999999 : 1300 },
                                },
                            }}
                            error={field.error}
                            helperText={field.error ? field.error_message : ''}
                            variant="outlined"
                            size="small"
                            defaultValue={field.default_value ? field.default_value : ''}
                            fullWidth
                            name={field.name}
                            onChange={handleChange}
                            // onInput={handleChange}
                            InputProps={{
                                className: config.layout.theme.fieldsType == 'bootstrap' ? classes.bootstrapField : '',
                            }}
                        >
                            {field.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <span className={classes.selectItem}>{option.title}</span>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                );
                break;
            case 'text-field':
                return (
                    <Grid key={field.name} item xs={12}>
                        {config.layout.theme.fieldsType == 'bootstrap' && field.label && (
                            <div style={{ marginBottom: 5 }}>
                                <label className={field.error ? classes.bootstrapFieldError : ''} htmlFor="{field.name}">
                                    {field.label}
                                    {field.required && field.label ? '*' : ''}
                                </label>
                            </div>
                        )}
                        <TextField
                            id={field.name}
                            required={field.required}
                            label={config.layout.theme.fieldsType == 'material' ? field.label : ''}
                            error={field.error}
                            helperText={field.error ? field.error_message : ''}
                            variant="outlined"
                            size="small"
                            placeholder={field.placeholder}
                            defaultValue={field.default_value ? field.default_value : ''}
                            fullWidth
                            name={field.name}
                            onInput={handleChange}
                            InputProps={{
                                className: config.layout.theme.fieldsType == 'bootstrap' ? classes.bootstrapField : '',
                            }}
                        />
                    </Grid>
                );
                break;
            case 'text-area':
                return (
                    <Grid key={field.name} item xs={12}>
                        {config.layout.theme.fieldsType == 'bootstrap' && field.label && (
                            <div style={{ marginBottom: 5 }}>
                                <label className={field.error ? classes.bootstrapFieldError : ''} htmlFor={field.name}>
                                    {field.label}
                                    {field.required && field.label ? '*' : ''}
                                </label>
                            </div>
                        )}
                        <TextField
                            id={field.name}
                            multiline={true}
                            rows={6}
                            required={field.required}
                            label={config.layout.theme.fieldsType == 'material' ? field.label : ''}
                            error={field.error}
                            placeholder={field.placeholder}
                            helperText={field.error ? field.error_message : ''}
                            variant="outlined"
                            size="small"
                            fullWidth
                            defaultValue={field.default_value ? field.default_value : ''}
                            InputProps={{
                                className: config.layout.theme.fieldsType == 'bootstrap' ? classes.bootstrapField : '',
                            }}
                            name={field.name}
                            onInput={handleChange}
                        />
                    </Grid>
                );
                break;
            case 'checkboxes':
                return (
                    <Grid key={field.name} item xs={12}>
                        {config.layout.theme.fieldsType == 'bootstrap' && field.label && (
                            <div style={{ marginBottom: 5 }}>
                                <label className={field.error ? classes.bootstrapFieldError : ''} htmlFor={field.name}>
                                    {field.label}
                                    {field.required && field.label ? '*' : ''}
                                </label>
                            </div>
                        )}
                        {field.options?.map((checkboxOptions, index) => {
                            return <FormControlLabel key={index}
                                control={
                                    <Checkbox
                                        name={checkboxOptions.name}
                                        onChange={handleChangeCheckbox(field)}
                                        checkedIcon={<span className={classes.checkboxCheckedIcon} />}
                                        icon={<span className={classes.checkboxIcon} />}
                                    />
                                }
                                label={checkboxOptions.title}
                            />;

                        })}
                    </Grid>

                );
        }
    };

    const renderCaptcha = () => {
        switch (config.captcha.type) {
            case 'google':
                return <div ref={grecaptcha}></div>;
                break;
            default:
                return (
                    <Grid item xs={12}>
                        {config.layout.theme.fieldsType == 'bootstrap' && (
                            <div style={{ marginBottom: 5 }}>
                                <label className={captchaStatus ? classes.bootstrapFieldError : ''} htmlFor={'captcha'}>
                                    Type in result please*
                                </label>
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'stretch' }}>
                            <TextField
                                id="captcha"
                                required={true}
                                label={config.layout.theme.fieldsType == 'material' ? 'Type in result please' : ''}
                                error={captchaStatus}
                                // helperText={captchaStatus ? 'Invalid captcha' : ''}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    className: config.layout.theme.fieldsType == 'bootstrap' ? classes.bootstrapField : '',
                                }}
                                className="request_quote_jCaptcha"
                                fullWidth
                            />
                        </div>
                        {/* ! todo add error message */}
                        {/* { captchaStatus && (
                            <div>
                                <p
                                    className='MuiFormHelperText-root MuiFormHelperText-contained Mui-error Mui-required MuiFormHelperText-marginDense'
                                    id='captcha-helper-text'
                                >
                                    INVALID CAPTCHA
                                </p>
                            </div>
                        )} */}
                    </Grid>
                );
                break;
        }
    };

    const initializeCaptcha = () => {
        try {
            if (config.captcha.type === 'google') {
                let key = config.captcha.settings.google.g_key;
                // key = '6Lf93JkaAAAAAFFbLOl_NMTAARAox3CLshv5rkxq';
                setCaptchaInstance(
                    window['grecaptcha'].render(grecaptcha.current, {
                        sitekey: key,
                        size: 'invisible',
                        callback: ((formReference) => (captchaResponse) => {
                            submitData(formReference.sosFormSubmitData);
                        })(formRef.current),
                    })
                );
            } else {
                setCaptchaInstance(
                    new jCaptcha({
                        el: '.request_quote_jCaptcha',
                        canvasClass: classes.request_quote_jCaptchaCanvas,
                        canvasStyle: {
                            // required properties for captcha stylings:
                            width: 100,
                            height: 15,
                            textBaseline: 'top',
                            font: '16px Arial',
                            textAlign: 'left',
                            fillStyle: '#000',
                        },
                    })
                );
            }
        } catch (error) {
            // console.log(error);
        }
        // your source code to load initial data
    };

    useEffect(() => {
        initializeCaptcha();

        config.layout.fields.map((field) => {
            if (field.default_value) {
                setFormData({
                    name: field.name,
                    value: field.default_value,
                });
            }
            return field;
        });

        // code to run after render goes here
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <div className={classes.root}>
                <div>
                    <h2>{config.layout.title}</h2>
                </div>
                {config.layout.description && (
                    <div>
                        <h3>{config.layout.description}</h3>
                    </div>
                )}
                {submitStatus === 'submit-error' && serverError?.error && (
                    <div className={classes.serverError}>
                        {serverError.error.map((err, index) => {
                            return <p key={index}>{err.message}</p>;
                        })}
                        {/* {serverError.error[0].message} */}
                    </div>
                )}
                {!(submitStatus === 'submit-success') ? (
                    <div>
                        <form noValidate autoComplete="off" ref={formRef} onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                {fields.map((input) => {
                                    if (!input.default_value) {
                                        return renderFields(input);
                                    }
                                })}
                                {renderCaptcha()}
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        color="secondary"
                                        className={classes.submitButton}
                                        disableRipple={true}
                                        disableElevation={true}
                                    >
                                        <span>{config.layout.button_text}</span>
                                        <span
                                            className="icon-arrow-right"
                                            style={{
                                                paddingTop: 3,
                                                paddingLeft: 15,
                                            }}
                                        ></span>
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                        {config.layout.footer_text && (
                            <div
                                style={{
                                    marginTop: 20,
                                }}
                            >
                                <h4 style={{ margin: 0 }}>{config.layout.footer_text}</h4>
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <p>{config.layout.submit_text}</p>
                    </div>
                )}
            </div>
        </ThemeProvider>
    );
};

export const quoteFormInit = (container, config: QuoteFormConfigDTO, backEndService: BackEndService) => {
    ReactDOM.unmountComponentAtNode(container);
    return ReactDOM.render(<QuoteForm config={config} backEndService={backEndService} />, container);
};

export const quoteFormRemove = (container) => {
    ReactDOM.unmountComponentAtNode(container);
};
